/**
 * @fileoverview Onboarding (/academy/onboarding): the Hans Academy's video
 * walkthroughs, grouped by the portal they teach. Not /onboarding, which is
 * the wizard a new account goes through after signup.
 *
 * A hub rather than a page per film, so the next walkthrough (instructor or
 * organization) is an entry in lib/walkthroughs.js and appears here on its
 * own. A portal with nothing published yet isn't listed at all: an empty
 * "coming soon" shelf tells a new instructor nothing they can use. Each card
 * opens the film's own page, which is the address to share.
 */

import { Link } from "react-router-dom";
import { PlayCircle } from "lucide-react";

import PublicLayout from "@/components/PublicLayout";
import { formatDuration, shelvesOf, walkthroughPath } from "@/lib/walkthroughs";

export default function AcademyOnboardingPage() {
  const shelves = shelvesOf();

  return (
    <PublicLayout>
      <main className="relative z-10 mx-auto max-w-5xl px-4 pb-24 pt-12 md:px-8 md:pt-20 safe-area-padding">
        <header className="mb-14 text-center">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-[var(--accent-yellow-text)]">Hans Academy</p>
          <h1 className="mb-4 font-serif text-4xl font-light tracking-tight text-[var(--text-primary)] md:text-5xl">Onboarding</h1>
          <p className="mx-auto max-w-xl font-sans text-base text-[var(--text-secondary)]">
            Short walkthroughs of the real app, step by step. Watch one before your first day, or send the link to
            someone who&apos;s just joining.
          </p>
        </header>

        {shelves.map((shelf) => (
          <section key={shelf.id} className="mb-14" aria-labelledby={`shelf-${shelf.id}`}>
            <h2 id={`shelf-${shelf.id}`} className="font-serif text-2xl font-light text-[var(--text-primary)]">
              {shelf.title}
            </h2>
            <p className="mb-6 mt-1 font-sans text-sm text-[var(--text-tertiary)]">{shelf.blurb}</p>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {shelf.films.map((film) => (
                <Link
                  key={film.slug}
                  to={walkthroughPath(film.slug)}
                  data-testid={`walkthrough-card-${film.slug}`}
                  className="group block overflow-hidden rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] transition duration-200 hover:border-[var(--studio-divider-strong)] active:scale-95"
                >
                  <div className="relative">
                    <img src={film.poster} alt="" loading="lazy" className="block aspect-video w-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-[var(--studio-scrim)] opacity-0 transition duration-200 group-hover:opacity-100">
                      <PlayCircle className="h-10 w-10 text-[var(--text-primary)]" strokeWidth={1.5} />
                    </span>
                    <span className="absolute bottom-2 right-2 rounded-full bg-[var(--studio-base)] px-2 py-0.5 font-mono text-[11px] text-[var(--text-primary)]">
                      {formatDuration(film.seconds)}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-lg leading-snug text-[var(--text-primary)]">{film.title}</h3>
                    <p className="mt-1.5 font-sans text-[13px] leading-relaxed text-[var(--text-secondary)]">{film.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}

        <p className="text-center font-sans text-sm text-[var(--text-tertiary)]">
          Prefer to read?{" "}
          <Link to="/guide" className="text-[var(--accent-yellow-text)] underline-offset-4 hover:underline">
            The instructor guide
          </Link>{" "}
          covers every page in detail.
        </p>
      </main>
    </PublicLayout>
  );
}
